import type { NormalizeOAS, OASRequestParams } from 'fets';
import type aiOpenApi from '../aiOpenApi';
import type { AiFetsApiClient } from '../aiFetsClient';
import type { ProjectVM } from '../aiOpenApi.types';
import { assertNever } from '../utils';

type Params = {
  source: 'ael' | 'preview' | 'public' | 'scorm';
  guid: string;
};

type Result = ProjectVM;

export type ApiFunction = (params: Params, signal?: AbortSignal) => Promise<Result>;

const WITH_SOURCE_PATH = '/api/editor/get-project-with-source/{courseGuid}';
const PUBLIC_PATH = '/api/editor/get-project/public/{guid}';

export const createGetProject = (apiClient: AiFetsApiClient): ApiFunction => {
  const getProject = async (params: Params, signal?: AbortSignal): Promise<Result> => {
    if (params.source === 'public') {
      const response = await apiClient[PUBLIC_PATH].get({
        params: { guid: params.guid },
        ...(signal && { signal }),
      });
      return convertResponse(response);
    }

    const response = await apiClient[WITH_SOURCE_PATH].get({
      ...convertToRequest(params.guid, params.source),
      ...(signal && { signal }),
    });
    return convertResponse(response);
  };

  return getProject;
};

type WithSourceRequest = OASRequestParams<NormalizeOAS<typeof aiOpenApi>, typeof WITH_SOURCE_PATH, 'get'>;

const convertToRequest = (guid: string, source: Exclude<Params['source'], 'public'>): WithSourceRequest => {
  switch (source) {
    case 'preview':
      return { params: { courseGuid: guid }, query: { source: 'EditorPreview' } };
    case 'scorm':
      return { params: { courseGuid: guid }, query: { source: 'DispatchedScorm' } };
    case 'ael':
      return { params: { courseGuid: guid }, query: { source: 'AelStudent' } };
  }
};

/**
 * Read as a plain Response: the schema's ProjectVM is too deep for fets to infer (TS2589),
 * so the body is typed with the hand-written ProjectVM instead.
 */
const convertResponse = async (response: Response): Promise<Result> => {
  if (response.status !== 200) {
    return assertNever(response.status);
  }

  return (await response.json()) as ProjectVM;
};
